import { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Skeleton } from '../ui/skeleton';
import { 
  Users, Eye, Target, TrendingUp, MapPin, Clock, Monitor, 
  Smartphone, Tablet, Globe, Search, RefreshCw, User, Mail,
  Activity, Zap, Sun, Shirt, Coffee, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

// Stats card component
function StatCard({ title, value, icon: Icon, trend, color = 'amber' }) {
  const colorClasses = {
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
    blue: 'bg-blue-50 text-blue-600 border-blue-200',
    green: 'bg-green-50 text-green-600 border-green-200',
    purple: 'bg-purple-50 text-purple-600 border-purple-200'
  };

  return (
    <Card className={`p-4 border ${colorClasses[color]}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold mt-1">{value}</p>
          {trend && (
            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-full ${colorClasses[color].split(' ')[0]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
}

// Device icon helper
function DeviceIcon({ device }) {
  switch (device?.toLowerCase()) {
    case 'mobile': return <Smartphone className="w-4 h-4" />;
    case 'tablet': return <Tablet className="w-4 h-4" />;
    default: return <Monitor className="w-4 h-4" />;
  }
}

// Service badge helper
function ServiceBadge({ service }) {
  const serviceConfig = {
    'Tanning': { icon: Sun, color: 'bg-amber-100 text-amber-700 border-amber-300' },
    'Laundry': { icon: Shirt, color: 'bg-blue-100 text-blue-700 border-blue-300' },
    '818 EATS': { icon: Coffee, color: 'bg-orange-100 text-orange-700 border-orange-300' },
    'Fizze': { icon: Coffee, color: 'bg-teal-100 text-teal-700 border-teal-300' }
  };
  const config = serviceConfig[service] || { icon: Globe, color: 'bg-gray-100 text-gray-700 border-gray-300' };
  const Icon = config.icon;

  return (
    <Badge variant="outline" className={`${config.color} border text-xs`}>
      <Icon className="w-3 h-3 mr-1" />
      {service}
    </Badge>
  );
}

export function VisitorIntelligence() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [visitors, setVisitors] = useState([]);
  const [liveVisitors, setLiveVisitors] = useState([]);
  const [highIntentVisitors, setHighIntentVisitors] = useState([]);
  const [funnels, setFunnels] = useState([]);
  const [selectedVisitor, setSelectedVisitor] = useState(null);
  const [visitorProfile, setVisitorProfile] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      const [statsRes, visitorsRes, liveRes, highIntentRes, funnelsRes] = await Promise.all([
        fetch(`${backendUrl}/api/identity/stats`),
        fetch(`${backendUrl}/api/identity/visitors?limit=100`),
        fetch(`${backendUrl}/api/identity/live`),
        fetch(`${backendUrl}/api/identity/high-intent`),
        fetch(`${backendUrl}/api/identity/funnels`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (visitorsRes.ok) {
        const data = await visitorsRes.json();
        setVisitors(data.visitors || []);
      }
      if (liveRes.ok) {
        const data = await liveRes.json();
        setLiveVisitors(data.visitors || []);
      }
      if (highIntentRes.ok) {
        const data = await highIntentRes.json();
        setHighIntentVisitors(data.high_intent_visitors || []);
      }
      if (funnelsRes.ok) {
        const data = await funnelsRes.json();
        setFunnels(data.funnels || []);
      }
    } catch (error) {
      console.error('Error fetching visitor data:', error);
      toast.error('Failed to load visitor data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => fetchData(true), 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchVisitorProfile = async (visitorId) => {
    try {
      const res = await fetch(`${backendUrl}/api/identity/visitors/${visitorId}`);
      if (res.ok) {
        const data = await res.json();
        setVisitorProfile(data);
        setShowProfileModal(true);
      }
    } catch (error) {
      toast.error('Failed to load visitor profile');
    }
  };

  const filteredVisitors = visitors.filter(v => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      v.visitor_id?.toLowerCase().includes(q) ||
      v.email?.toLowerCase().includes(q) ||
      v.name?.toLowerCase().includes(q) ||
      v.city?.toLowerCase().includes(q)
    );
  });

  // Format relative time
  const formatRelativeTime = (isoString) => {
    if (!isoString) return 'Unknown';
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Skeleton key={i} className="h-24 rounded-lg" />
          ))}
        </div>
        <Skeleton className="h-96 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="visitor-intelligence">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold font-serif flex items-center gap-2">
            <Eye className="w-6 h-6 text-amber-500" />
            Visitor Intelligence
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Track anonymous visitors, identify high-intent prospects, and manage conversion funnels
          </p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => fetchData(true)} 
          disabled={refreshing}
          data-testid="refresh-visitors"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard 
          title="Total Visitors" 
          value={stats?.total_visitors || 0} 
          icon={Users} 
          color="blue"
        />
        <StatCard 
          title="Known Customers" 
          value={stats?.known_visitors || 0} 
          icon={User} 
          trend={stats?.total_visitors ? `${((stats.known_visitors / stats.total_visitors) * 100).toFixed(1)}% converted` : null}
          color="green"
        />
        <StatCard 
          title="Today's Visits" 
          value={stats?.today_visits || 0} 
          icon={Activity} 
          color="amber"
        />
        <StatCard 
          title="In Funnels" 
          value={stats?.funnel_members || 0} 
          icon={Target} 
          color="purple"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="overview" data-testid="tab-overview">
            <Activity className="w-4 h-4 mr-2" />
            Live ({liveVisitors.length})
          </TabsTrigger>
          <TabsTrigger value="high-intent" data-testid="tab-high-intent">
            <Zap className="w-4 h-4 mr-2" />
            High Intent ({highIntentVisitors.length})
          </TabsTrigger>
          <TabsTrigger value="funnels" data-testid="tab-funnels">
            <Target className="w-4 h-4 mr-2" />
            Funnels ({funnels.length})
          </TabsTrigger>
          <TabsTrigger value="all-visitors" data-testid="tab-all-visitors">
            <Users className="w-4 h-4 mr-2" />
            All Visitors
          </TabsTrigger>
        </TabsList>

        {/* Live Visitors Tab */}
        <TabsContent value="overview">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                Live Visitors (Last 5 minutes)
              </h3>
            </div>
            {liveVisitors.length > 0 ? (
              <div className="space-y-3">
                {liveVisitors.map((visitor) => (
                  <div 
                    key={visitor.visitor_id}
                    className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200 hover:bg-green-100 cursor-pointer transition-colors"
                    onClick={() => fetchVisitorProfile(visitor.visitor_id)}
                  >
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-green-100 rounded-full">
                        <DeviceIcon device={visitor.device} />
                      </div>
                      <div>
                        <p className="font-medium">
                          {visitor.is_known ? (visitor.name || visitor.email) : `Anonymous (${visitor.visitor_id.substring(0, 8)}...)`}
                        </p>
                        <p className="text-sm text-gray-500 flex items-center gap-2">
                          <MapPin className="w-3 h-3" />
                          {visitor.city || 'Unknown'}, {visitor.state || ''}
                          <span className="mx-1">•</span>
                          <Clock className="w-3 h-3" />
                          {formatRelativeTime(visitor.last_seen)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="bg-green-100 text-green-700">
                        {visitor.current_page || '/'}
                      </Badge>
                      {visitor.is_known && (
                        <Badge className="bg-blue-500">Known</Badge>
                      )}
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No visitors in the last 5 minutes</p>
                <p className="text-sm">Visitors will appear here in real-time</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* High Intent Tab (Tanning Prioritized) */}
        <TabsContent value="high-intent">
          <Card className="p-4">
            <div className="mb-4">
              <h3 className="font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-500" />
                High-Intent Visitors
              </h3>
              <p className="text-sm text-gray-500">Visitors with multiple visits or tanning interest (priority funnel)</p>
            </div>
            {highIntentVisitors.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="high-intent-table">
                  <thead className="bg-amber-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Visitor</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Visits</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Services Viewed</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Last Seen</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {highIntentVisitors.map((visitor, idx) => (
                      <tr 
                        key={visitor.visitor_id} 
                        className={`${idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'} hover:bg-amber-100 cursor-pointer`}
                        onClick={() => fetchVisitorProfile(visitor.visitor_id)}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <DeviceIcon device={visitor.device} />
                            <div>
                              <p className="font-medium text-sm">
                                {visitor.is_known ? (visitor.name || visitor.email) : `Anon-${visitor.visitor_id.substring(0, 6)}`}
                              </p>
                              {visitor.email && (
                                <p className="text-xs text-gray-500">{visitor.email}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {visitor.city}, {visitor.state}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{visitor.total_visits}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {(visitor.services_viewed || []).map(service => (
                              <ServiceBadge key={service} service={service} />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {formatRelativeTime(visitor.last_seen)}
                        </td>
                        <td className="px-4 py-3">
                          {visitor.is_known ? (
                            <Badge className="bg-green-500">Known</Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-400 text-amber-600">Anonymous</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No high-intent visitors yet</p>
              </div>
            )}
          </Card>
        </TabsContent>

        {/* Funnels Tab */}
        <TabsContent value="funnels">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Tanning Interest Funnel (Priority) */}
            <Card className="p-4 border-2 border-amber-300 bg-amber-50/50">
              <div className="flex items-center gap-2 mb-4">
                <Sun className="w-5 h-5 text-amber-600" />
                <h3 className="font-semibold text-amber-800">Tanning Interest</h3>
                <Badge className="ml-auto bg-amber-500">Priority</Badge>
              </div>
              {funnels.find(f => f.funnel_id === 'tanning_interest')?.members?.length > 0 ? (
                <div className="space-y-2">
                  {funnels.find(f => f.funnel_id === 'tanning_interest').members.slice(0, 5).map((member) => (
                    <div key={member.visitor_id} className="flex items-center justify-between p-2 bg-white rounded border">
                      <div>
                        <p className="font-medium text-sm">{member.name || member.email || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">Entered: {formatRelativeTime(member.entered_at)}</p>
                      </div>
                      {member.email && (
                        <Badge variant="outline" className="text-xs">
                          <Mail className="w-3 h-3 mr-1" />
                          Has Email
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No members yet</p>
              )}
            </Card>

            {/* Other Funnels */}
            {funnels.filter(f => f.funnel_id !== 'tanning_interest').map((funnel) => (
              <Card key={funnel.funnel_id} className="p-4">
                <div className="flex items-center gap-2 mb-4">
                  <Target className="w-5 h-5 text-gray-600" />
                  <h3 className="font-semibold">{funnel.funnel_name}</h3>
                  <Badge variant="outline" className="ml-auto">{funnel.members?.length || 0}</Badge>
                </div>
                {funnel.members?.length > 0 ? (
                  <div className="space-y-2">
                    {funnel.members.slice(0, 3).map((member) => (
                      <div key={member.visitor_id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <p className="font-medium text-sm">{member.name || member.email || 'Unknown'}</p>
                        <p className="text-xs text-gray-500">{formatRelativeTime(member.entered_at)}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No members yet</p>
                )}
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* All Visitors Tab */}
        <TabsContent value="all-visitors">
          <Card className="p-4">
            <div className="flex items-center gap-4 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by ID, email, name, or city..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="visitor-search"
                />
              </div>
              <Badge variant="outline">{filteredVisitors.length} visitors</Badge>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full" data-testid="visitors-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Visitor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Device</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Location</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Visits</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">First Seen</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Last Seen</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredVisitors.slice(0, 50).map((visitor, idx) => (
                    <tr 
                      key={visitor.visitor_id} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'} hover:bg-blue-50 cursor-pointer`}
                      onClick={() => fetchVisitorProfile(visitor.visitor_id)}
                    >
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium text-sm">
                            {visitor.is_known ? (visitor.name || visitor.email) : `Anon-${visitor.visitor_id.substring(0, 8)}`}
                          </p>
                          {visitor.email && (
                            <p className="text-xs text-gray-500">{visitor.email}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <DeviceIcon device={visitor.device} />
                          <span className="text-sm capitalize">{visitor.device || 'Unknown'}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {visitor.city || 'Unknown'}{visitor.state ? `, ${visitor.state}` : ''}
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant="outline">{visitor.total_visits || 1}</Badge>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatRelativeTime(visitor.first_seen)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatRelativeTime(visitor.last_seen)}
                      </td>
                      <td className="px-4 py-3">
                        {visitor.is_known ? (
                          <Badge className="bg-green-500">Known</Badge>
                        ) : (
                          <Badge variant="outline">Anonymous</Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Visitor Profile Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Visitor Profile
            </DialogTitle>
          </DialogHeader>
          
          {visitorProfile && (
            <div className="space-y-6">
              {/* Profile Header */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="p-3 bg-white rounded-full border">
                  <DeviceIcon device={visitorProfile.visitor?.device} />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-lg">
                    {visitorProfile.visitor?.is_known 
                      ? (visitorProfile.visitor.name || visitorProfile.visitor.email)
                      : `Anonymous Visitor`
                    }
                  </h3>
                  <p className="text-sm text-gray-500">
                    ID: {visitorProfile.visitor?.visitor_id}
                  </p>
                </div>
                {visitorProfile.visitor?.is_known && (
                  <Badge className="bg-green-500">Converted</Badge>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{visitorProfile.visitor?.email || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{visitorProfile.visitor?.phone || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Location</p>
                  <p className="font-medium">
                    {visitorProfile.visitor?.city || 'Unknown'}, {visitorProfile.visitor?.state || ''}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Visits</p>
                  <p className="font-medium">{visitorProfile.visitor?.total_visits || 1}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">First Seen</p>
                  <p className="font-medium">{formatRelativeTime(visitorProfile.visitor?.first_seen)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Seen</p>
                  <p className="font-medium">{formatRelativeTime(visitorProfile.visitor?.last_seen)}</p>
                </div>
              </div>

              {/* Funnels */}
              {visitorProfile.funnels?.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-2">Active Funnels</h4>
                  <div className="flex flex-wrap gap-2">
                    {visitorProfile.funnels.map(f => (
                      <Badge key={f.funnel_id} className="bg-purple-100 text-purple-700">
                        {f.funnel_name}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Event Timeline */}
              <div>
                <h4 className="font-semibold mb-2">Recent Activity</h4>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {(visitorProfile.events || []).slice(0, 20).map((event, idx) => (
                    <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded text-sm">
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                      <div className="flex-1">
                        <span className="font-medium capitalize">{event.event_type?.replace('_', ' ')}</span>
                        <span className="text-gray-500 ml-2">{event.page}</span>
                      </div>
                      <span className="text-gray-400 text-xs">
                        {formatRelativeTime(event.timestamp)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
